import { Badge, Card, CardContent, CardHeader, CardTitle, SkeletonRows } from '@crm/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCrmClient } from '../../../api/client.ts';
import { queryKeys } from '../../../api/keys.ts';
import { useOrgId } from '../../../api/session.ts';
import { NotWired } from '../../../components/not-wired.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';

export const Route = createFileRoute('/_authed/settings/organization')({
  component: OrganizationSettings,
  errorComponent: RouteErrorPanel,
});

function OrganizationSettings() {
  const client = useCrmClient();
  const orgId = useOrgId();

  const orgs = useQuery({
    queryKey: queryKeys.orgs(orgId),
    queryFn: () => client.org.list({}),
  });

  const settings = useQuery({
    queryKey: queryKeys.orgSettings(orgId),
    queryFn: () => client.org.settings.get({ orgId: orgId ?? '' }),
    enabled: orgId !== null,
  });

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Organizations</CardTitle>
        </CardHeader>
        <CardContent>
          {orgs.isLoading ? (
            <SkeletonRows rows={2} />
          ) : orgs.isError ? (
            <p className="text-sm text-muted-foreground">Could not load organizations.</p>
          ) : (
            <ul className="flex flex-col gap-2 text-sm">
              {(orgs.data?.items ?? []).map((org) => (
                <li key={org.id} className="flex items-center gap-2">
                  <span className="font-medium">{org.name}</span>
                  <Badge variant="muted">{org.role}</Badge>
                  {org.isActive ? <Badge variant="secondary">active</Badge> : null}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {org.memberCount} member(s)
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Org settings</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {settings.isLoading ? (
            <SkeletonRows rows={3} />
          ) : settings.data === undefined ? (
            <p className="text-muted-foreground">Not available.</p>
          ) : (
            <dl className="grid grid-cols-[16rem_1fr] gap-y-1">
              <dt className="text-muted-foreground">Timezone</dt>
              <dd>{settings.data.timezone}</dd>
              <dt className="text-muted-foreground">Enrichment credit cap / month</dt>
              <dd>{settings.data.enrichmentMonthlyCreditCap}</dd>
              <dt className="text-muted-foreground">Auto-enrich on create</dt>
              <dd>{settings.data.enrichmentAutoOnCreate ? 'on' : 'off'}</dd>
              <dt className="text-muted-foreground">Share enriched fields</dt>
              <dd>{settings.data.allowEnrichedFieldSharing ? 'on' : 'off (legal default)'}</dd>
              <dt className="text-muted-foreground">Recording consent mode</dt>
              <dd>{settings.data.recordingDefaultConsentMode}</dd>
            </dl>
          )}
        </CardContent>
      </Card>

      <NotWired
        title="Editing settings, members and invitations"
        blockedOn="org.settings.update exists but is read-only here; member/invitation management is a better-auth organization surface with no CRM contract procedure"
      />
    </div>
  );
}
